import { isPlatformBrowser, Location, LocationStrategy } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    NavigationExtras,
    Params,
    Route,
    Router,
    RouterEvent,
    Routes,
    UrlTree,
} from '@angular/router';
import { BehaviorSubject, filter, map, Observable, of, switchMap, timeout } from 'rxjs';

import { BcoService, EnvironmentService } from '@ecm/consumer';
import { EcmDispatcherModule, EcmGlobalThisUtil, EnvUtils, StoreService } from '@ecm/dispatcher';
import { ContextService, Contract } from '@ecm/portfolio';

import { RouteType } from '../business/route-type.enum';
import { ErrorPageService } from './error-page.service';

@Injectable({
    providedIn: 'root',
})
export class RouterService {
    private static CLASS_NAME = 'RouterService';

    /**
     * Timeout in ms for the BCO request of the external redirect context
     * @type {number}
     */
    private readonly TIMEOUT_BEFORE_REDIRECT: number = 500;

    /**
     * Is Sosh internal flag
     * @type {boolean}
     */
    private isSosh: boolean = null;

    private _isSosh$ = new BehaviorSubject<boolean>(false);

    /**
     * LocationStrategy.onPopState callback flag
     * @type {boolean}
     */
    protected isPopState = false;

    /**
     * Visitor routes
     */
    private visitorRoutes: Routes = [];

    /**
     * Orange Pro/OBS HPs
     */
    private readonly EC_PRO_URL: string = 'https://espaceclientpro.orange.fr/';
    private readonly OBL_URL: string = 'https://businesslounge.orange.fr';

    public constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        private router: Router,
        private location: Location,
        private locationStrategy: LocationStrategy,
        private errorPageService: ErrorPageService,
        private activatedRoute: ActivatedRoute,
        private contextService: ContextService,
        private bcoService: BcoService,
        private environmentService: EnvironmentService,
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.manageNavigationEnd();
            this.filterRoutes();
        }
    }

    /**
     * Observable that emits a boolean value indicating whether the current state is Sosh2.
     *
     * @returns {Observable<boolean>} An observable that emits true if the current state is Sosh2, otherwise false.
     */
    public get isSosh$(): Observable<boolean> {
        return this._isSosh$;
    }

    /**
     * Sets the value of `_isSosh$` observable.
     *
     * @param isSosh - A boolean value to be emitted by the `_isSosh$` observable.
     */
    public setIsSosh(isSosh: boolean) {
        this.isSosh = isSosh;
        this._isSosh$.next(isSosh);
    }

    /**
     * Checks if current route is Sosh
     * based on `ContextService` from `ecm-portfolio` for logged routes
     * and presence of query param `sosh` for visitor routes
     * @returns {Observable<boolean>}
     */
    public isCurrentRouteSosh(): Observable<boolean> {
        if (this.isSosh === null && this.isCurrentRouteVisitor()) {
            return this.activatedRoute.queryParams.pipe(
                map((params: Params) => {
                    const lowerCaseParams: Params = {};
                    for (const key of Object.keys(params)) {
                        lowerCaseParams[key.toLowerCase()] = params[key];
                    }
                    return lowerCaseParams;
                }),
                map((params: Params) => Object.prototype.hasOwnProperty.call(params, 'sosh')),
            );
        } else {
            // Value is handled by manageNavigationEnd
            return of(this.isSosh);
        }
    }

    /**
     * Checks if current route is a VISITOR route
     * @returns {boolean}
     */
    public isCurrentRouteVisitor(): boolean {
        return this.findPathInRoutes(this.visitorRoutes, this.router.url);
    }

    /**
     * Navigates to a given route
     * Simple wrapper of Angular Router navigate method
     * @param {string[]} commands
     * @param {NavigationExtras} extras (optional)
     * @returns {Promise<boolean>}
     */
    public navigate(commands: string[], extras?: NavigationExtras): Promise<boolean> {
        return this.router.navigate(commands, extras);
    }

    /**
     * Simple wrapper of Angular Router navigateByUrl method
     * @param {string | UrlTree} url
     * @param {NavigationExtras} [extras]
     * @returns {Promise<boolean>}
     */
    public navigateByUrl(url: string | UrlTree, extras?: NavigationExtras): Promise<boolean> {
        return this.router.navigateByUrl(url, extras);
    }

    /**
     * Navigates back in browser history
     */
    public navigateBack(): void {
        this.location.back();
    }

    /**
     * Navigates to HP
     */
    public navigateToHome(): void {
        this.router.navigate(['accueil']);
    }

    /**
     * Redirects to an external URL with waiting for BCO logs to be sent
     * @param {string} url
     */
    public redirectToExternalUrl(url: string): void {
        if (isPlatformBrowser(this.platformId)) {
            // flush BCO logs before redirect
            this.bcoService
                .flushLogs()
                .pipe(timeout({ first: this.TIMEOUT_BEFORE_REDIRECT, with: () => of(true) }))
                .subscribe(() => {
                    EcmDispatcherModule.sendConsoleInfo(
                        RouterService.CLASS_NAME,
                        'Will redirect current tab to URL ' + url,
                    );
                    EcmGlobalThisUtil.setLocationHref(EcmDispatcherModule.handlePreviousPageLink(url));
                });
        } else {
            EcmDispatcherModule.sendConsoleError(
                RouterService.CLASS_NAME,
                "Can't redirect to external URL on server platform",
            );
        }
    }

    /**
     * Redirects to an external URL in new tab
     * @param {string} url
     */
    public redirectToExternalTabUrl(url: string): void {
        if (isPlatformBrowser(this.platformId)) {
            // flush BCO logs before redirect
            this.bcoService.flushLogs();
            EcmDispatcherModule.sendConsoleInfo(RouterService.CLASS_NAME, 'Will open a new tab to URL ' + url);
            window.open(url, '_blank');
        } else {
            EcmDispatcherModule.sendConsoleError(RouterService.CLASS_NAME, "Can't open a new tab on server platform");
        }
    }

    /**
     * Redirects current tab to Espace Client Pro
     */
    public redirectToECPro(): void {
        const url = this.environmentService.instant('EC_PRO_URL') || this.EC_PRO_URL;
        EcmDispatcherModule.sendConsoleInfo(RouterService.CLASS_NAME, 'Will redirect current tab to URL ' + url);
        this.redirectToExternalUrl(url);
    }

    /**
     * Redirects current tab to Orange Business Lounge
     */
    public redirectToOBL(): void {
        EcmDispatcherModule.sendConsoleInfo(
            RouterService.CLASS_NAME,
            'Will redirect current tab to URL ' + this.OBL_URL,
        );
        this.redirectToExternalUrl(this.OBL_URL);
    }

    /**
     * Redirects to Orange authentication page
     * based on environment key `AUTH_URL`
     * need to use encodeUriComponent to manage '&' query params
     * In ECLAIR mode, just show Unauthorized page
     *
     * @param {string} [returnToUrl]   Specify this parameter if the returnTo url is not the current url
     * @param {HttpParams} [queryParams]    Additional query params to be added on auth url
     */
    public redirectToAuthenticationPage(returnToUrl?: string, queryParams?: HttpParams): void {
        if (EnvUtils.isEclair()) {
            this.errorPageService.showUnauthorizedPage();
        } else {
            const env = this.environmentService.instant();
            const computedReturnToUrl = returnToUrl ?? window.location.href;
            if (env.AUTH_URL) {
                const authUrl: string = env.AUTH_URL;
                let redirectionUrl = authUrl.concat(this.fixedEncodeURIComponent(computedReturnToUrl));

                if (queryParams) {
                    redirectionUrl = `${redirectionUrl}&${queryParams.toString()}`;
                }

                EcmDispatcherModule.sendConsoleInfo(
                    RouterService.CLASS_NAME,
                    'Will redirect after authentication to external URL ' + redirectionUrl,
                );
                this.redirectToExternalUrl(redirectionUrl);
            } else {
                this.errorPageService.showUnauthorizedPage(computedReturnToUrl);
            }
        }
    }

    /**
     * Sets currentContract in StoreService
     * @param {Contract} contract to store as current contract
     */
    public storeAndDispatchCurrentContract(contract: Contract): void {
        // object keys may be different if called from sendContractOpened or checkIdentity
        const currentContract = {
            cid: contract.cid,
            brand: contract.brand,
            offer: contract.offerName,
        };
        const logInfo: string =
            'will set contract ' + currentContract['cid'] + ' (' + currentContract['brand'] + ') as current contract';
        EcmDispatcherModule.sendConsoleInfo(RouterService.CLASS_NAME, logInfo);
        StoreService.set('currentContract', currentContract);
        EcmDispatcherModule.sendContractOpened(currentContract);
    }

    /**
     * To be more stringent in adhering to RFC 3986 (which reserves !, ', (, ), and *) ....
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
     * @param str
     * @private
     */
    private fixedEncodeURIComponent(str: string): string {
        return encodeURIComponent(str).replace(/[!'()*]/g, (c) => {
            return '%' + c.charCodeAt(0).toString(16);
        });
    }

    /**
     * Filter and extracts routes by RouteType
     */
    private filterRoutes(): void {
        // get all routes except default empty route
        const allRoutes: Routes = this.router.config.filter((route: Route) => route.path !== '');
        // reset routes arrays before extraction
        this.visitorRoutes = [];
        // for each Feature Routes, extract type-related routes in results arrays
        this.extractRoutes(allRoutes, RouteType.VISITOR, this.visitorRoutes);
    }

    /**
     * Recursive extraction of Routes by RouteType
     * parses Routes and performs extraction if RouteType matches
     * @param {Routes} routes Array of Routes to parse
     * @param {RouteType} routeType RouteType to match
     * @param {Routes} result Result Array to extract matched Route
     * @returns {Routes}
     */
    private extractRoutes(routes: Routes, routeType: RouteType, result: Routes): Routes {
        return routes.filter((route: Route) => {
            if (route.data && route.data.type && route.data.type === routeType) {
                // if RouteType matches, push Route to result Array
                result.push(route);
                return true;
            } else {
                if (route.children) {
                    // if no match, go deep and search again in routes children
                    this.extractRoutes(route.children, routeType, result);
                }
                return false;
            }
        }, this);
    }

    /**
     * Finds first route matching given path, in a given Routes Array
     * @param {Routes} routes Routes Array to search in
     * @param {string} path Route path to search
     * @returns {boolean}
     */
    private findPathInRoutes(routes: Routes, path: string): boolean {
        return routes.find((route: Route) => path.indexOf(route.path) !== -1) !== undefined;
    }

    /**
     * NavigationEnd event operations :
     * Fixes scrolling issues on route change (Resets scroll position to the top of document)
     * Updates isSosh state
     */
    private manageNavigationEnd(): void {
        this.locationStrategy.onPopState(() => {
            this.isPopState = true;
        });

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                switchMap((event: NavigationEnd) => {
                    return this.contextService.isSosh().pipe(
                        map((isSosh) => {
                            return { event, isSosh };
                        }),
                    );
                }),
            )
            .subscribe(({ event, isSosh }: { event: RouterEvent; isSosh: boolean }) => {
                // we change focus only if there is no hash in the url (keep anchor mechanism)
                if (!/#[a-z0-9_.-]+/i.test(event.url)) {
                    EcmDispatcherModule.sendConsoleInfo(
                        RouterService.CLASS_NAME,
                        'NavigationEnd : scroll window to top',
                    );

                    const srPageTitleNode: HTMLElement = document.querySelector<HTMLElement>('#sr-page-title');
                    if (srPageTitleNode !== null) {
                        // There is a bug with focus on Safari, it doesn't scroll well to focused element.
                        // We prevent autoscroll on focus for browser supporting it and use window.scroll that works everywhere
                        srPageTitleNode.focus({ preventScroll: true });
                        window.scroll(0, 0);
                    } else if (!this.isPopState) {
                        // Scroll to top if accessing a page, not via browser history stack
                        window.scroll(0, 0);
                    }
                }
                // Ensures that isPopState is reset
                this.isPopState = false;

                // Handle isSosh when route initialise or change
                this.setIsSosh(isSosh);
            });
    }
}
